import { cn, copyText } from "utils";

import { useState } from "react";
import { IconHover } from "../IconHover";
import { I18nUtil } from "@utils";
import Translations from "@translate/components/Chat/Action";
import { useCheckAccessClick } from "@/src/hooks/useCheckAccessClick";
import { useFeedback } from "@hooks";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";

const I18n = I18nUtil.createScoped(Translations);

const OR_SCENE = "Chat";

const Action = ({
  reload,
  markdown,
  messageId,
  onShare,
  rating,
  retry,
  isDeepSearchAgent,
  isTab,
}: {
  reload: () => void;
  onShare: () => void;
  markdown: string;
  messageId: string;
  rating: "thumb_up" | "thumb_down";
  retry: boolean;
  isDeepSearchAgent: boolean;
  isTab?: boolean;
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const [like, setLike] = useState({
    isUpActive: rating === "thumb_up",
    isDownActive: rating === "thumb_down",
  });
  const { feedback } = useFeedback();

  const { handleAccessClick: handleShareClick } = useCheckAccessClick({
    originClick: () => {
      onShare();
    },
  });

  const { handleAccessClick: handleLikeUpClick } = useCheckAccessClick({
    originClick: () => {
      feedback(messageId, "thumb_up");
      setLike({
        isUpActive: true,
        isDownActive: false,
      });
    },
  });

  const { handleAccessClick: handleLikeDownClick } = useCheckAccessClick({
    originClick: () => {
      feedback(messageId, "thumb_down");
      setLike({
        isUpActive: false,
        isDownActive: true,
      });
    },
  });

  const copy = () => {
    copyText(markdown);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className="flex items-center py-3 cursor-pointer gap-6">
      {!isDeepSearchAgent && (
        <>
          <div className="flex gap-4">
            {isCopied ? (
              <IconHover
                title={I18n.t("Copied")}
                name="check"
                className="text-content_quaternary cursor-pointer"
                hoverClassName={isTab ? "left-[24px]" : ""}
              />
            ) : (
              <IconHover
                data-openreplay-label={buildOpenReplayLabel(
                  OR_SCENE,
                  "MessageActions",
                  "Click",
                  I18n.t("Copy"),
                )}
                title={I18n.t("Copy")}
                name="copy"
                className="text-content_quaternary cursor-pointer"
                onClick={copy}
                hoverClassName={isTab ? "left-[22px]" : ""}
              />
            )}
            {retry && (
              <IconHover
                data-openreplay-label={buildOpenReplayLabel(
                  OR_SCENE,
                  "MessageActions",
                  "Click",
                  I18n.t("Regenerate"),
                )}
                title={I18n.t("Regenerate")}
                name="revision"
                className="text-content_quaternary cursor-pointer"
                onClick={reload}
              />
            )}
            <IconHover
              data-openreplay-label={buildOpenReplayLabel(
                OR_SCENE,
                "MessageActions",
                "Click",
                I18n.t("Share"),
              )}
              title={I18n.t("Share")}
              onClick={handleShareClick}
              name="expand-alt"
              className="text-content_quaternary cursor-pointer"
            />
          </div>
          <div className="w-[1px] bg-background_white_overlay10 h-5" />
        </>
      )}

      <div className="flex gap-4">
        <IconHover
          data-openreplay-label={buildOpenReplayLabel(
            OR_SCENE,
            "MessageFeedback",
            "Click",
            I18n.t("Best reply"),
          )}
          title={I18n.t("Best reply")}
          name={like.isUpActive ? "thumbs-up-filled" : "thumbs-up"}
          className={cn(
            "text-content_quaternary cursor-pointer",
            like.isUpActive ? "text-content_brand_primary" : "",
          )}
          onClick={handleLikeUpClick}
        />
        <IconHover
          data-openreplay-label={buildOpenReplayLabel(
            OR_SCENE,
            "MessageFeedback",
            "Click",
            I18n.t("Dislike"),
          )}
          title={I18n.t("Dislike")}
          name={like.isDownActive ? "thumbs-down-filled" : "thumbs-down"}
          className={cn(
            "text-content_quaternary cursor-pointer",
            like.isDownActive ? "text-content_brand_primary" : "",
          )}
          onClick={handleLikeDownClick}
        />
      </div>
    </div>
  );
};

export default Action;
